import React, { FC, useCallback, useContext, useMemo } from 'react';

import { EvSection } from '@evinced-private/ui-common';

import { parseTimeStr } from '../../helpers/SchedulingHelper';
import { getClientTimezoneValue } from '../../helpers/timezone/TimezoneHelper';
import {
	FrequencyDto,
	IScheduledJobConfiguration,
	JobTypeDto
} from '../../interfaces/ScheduledJobConfiguration';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';

import { SchedulingSectionContext } from './context/schedulingSectionContext';
import { SchedulingSectionContextWrapper } from './context/SchedulingSectionContextWrapper';
import { EmailSettings } from './EmailSettings';
import { getRoundedUpTime } from './helpers';
import { SchedulingSectionProps } from './SchedulingSection.types';
import { SectionHeader } from './SectionHeader';
import { SectionMainBlockWithContext } from './SectionMainBlockWithContext';

import './SchedulingSection.scss';

const EmailSettingsWithContext: FC<{ showEmailNotifications: boolean }> = ({
	showEmailNotifications
}) => {
	const { config } = useContext(SchedulingSectionContext);

	if (!showEmailNotifications || !config.active) {
		return null;
	}

	return <EmailSettings />;
};

export const SchedulingSection: FC<SchedulingSectionProps> = (props) => {
	const { initialConfig, onConfigChange: onConfigChangeProps, error, isListMode = false } = props;
	const { getToggle } = useConfiguration();
	const showEmailNotifications = getToggle(SiteScannerUiToggles.ENABLE_EMAIL_NOTIFICATIONS);

	const onConfigChange = useCallback(
		(config: IScheduledJobConfiguration) => {
			const { active } = config;
			let newConfig: IScheduledJobConfiguration = { ...config };
			if (!active) {
				newConfig = { ...initialConfig, active };
			}
			// "Crawl before scanning" does not apply when the property is in List mode.
			// Normalize jobType to SCAN to prevent persisting a contradictory CRAWL/CRAWL_AND_SCAN value.
			if (isListMode && newConfig.jobType !== JobTypeDto.SCAN) {
				newConfig = { ...newConfig, jobType: JobTypeDto.SCAN };
			}
			onConfigChangeProps(newConfig);
		},
		[onConfigChangeProps, initialConfig, isListMode]
	);

	const { active, frequency, interval, jobType, timezone, hours, minutes, ...rest } =
		initialConfig || {};

	const configWithDefaults = useMemo(() => {
		const defaultTime = getRoundedUpTime();
		const { hours: defaultHours, minutes: defaultMinutes } = parseTimeStr(
			defaultTime.value as string
		);

		// In List mode "Crawl before scanning" doesn't apply, so the job is always a pure SCAN.
		// In Crawl mode, use the stored jobType or fall back to CRAWL_AND_SCAN.
		const resolvedJobType = isListMode ? JobTypeDto.SCAN : jobType || JobTypeDto.CRAWL_AND_SCAN;

		return {
			active: active || false,
			frequency: frequency || FrequencyDto.WEEKLY,
			interval: interval || 1,
			jobType: resolvedJobType,
			timezone: timezone || getClientTimezoneValue(),
			hours: hours || [defaultHours],
			minutes: minutes || [defaultMinutes],
			...rest
		};
	}, [active, frequency, interval, jobType, timezone, hours, minutes, rest, isListMode]);

	return (
		<EvSection>
			<div className="scheduling-section-wrapper">
				<fieldset
					className="scheduling-section-fieldset"
					aria-labelledby="scheduling-section-title"
				>
					<SchedulingSectionContextWrapper
						initialConfig={configWithDefaults}
						onConfigChange={onConfigChange}
						error={error}
						isListMode={isListMode}
					>
						<SectionHeader />
						<SectionMainBlockWithContext />
						<EmailSettingsWithContext showEmailNotifications={showEmailNotifications} />
					</SchedulingSectionContextWrapper>
				</fieldset>
			</div>
		</EvSection>
	);
};
